import { useEffect, useRef, useState } from "react";

/**
 * Minimalistischer Custom Cursor (nur Desktop mit Feinzeiger):
 * kleiner Goldpunkt folgt direkt, Ring träge dahinter (Feder-Nachlauf).
 * Über interaktiven Elementen reagiert der Ring subtil (cursor-hot).
 * Wird auf Touch-Geräten und bei prefers-reduced-motion gar nicht erst gerendert.
 */
export default function CustomCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (!window.matchMedia("(pointer: fine)").matches) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    setEnabled(true);
    return undefined;
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const dot = dotRef.current;
    const ring = ringRef.current;
    if (!dot || !ring) return undefined;

    document.documentElement.classList.add("custom-cursor-active");
    let x = -100;
    let y = -100;
    let rx = -100;
    let ry = -100;
    let raf;

    const onMove = (e) => {
      x = e.clientX;
      y = e.clientY;
      dot.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      const hot = e.target.closest?.("a, button, [role='button'], input, textarea, select, label, [data-cursor]");
      ring.classList.toggle("cursor-hot", !!hot);
    };
    const loop = () => {
      rx += (x - rx) * 0.16;
      ry += (y - ry) * 0.16;
      ring.style.transform = `translate3d(${rx}px, ${ry}px, 0)`;
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      document.documentElement.classList.remove("custom-cursor-active");
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <>
      <div ref={dotRef} className="cursor-dot" data-testid="custom-cursor-dot" aria-hidden="true" />
      <div ref={ringRef} className="cursor-ring" data-testid="custom-cursor-ring" aria-hidden="true" />
    </>
  );
}

